import { useQuery } from '@tanstack/react-query';
import { type FixedDepartureDetail, type FixedDepartureFlightOut } from '@utazzo/shared';
import { UzButton, UzDatePicker, UzEmptyState, UzInput, UzSelect } from '@utazzo/ui';
import { ArrowRight, Plane, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import {
  useCreateFixedDepartureFlight,
  useDeleteFixedDepartureFlight,
  useUpdateFixedDepartureFlight,
} from '../use-fixed-departures';

import { CityPicker } from '@/components/pickers';
import { useAirlinesList } from '@/features/airlines/use-airlines';
import { airportOptionsOf } from '@/features/cities/airport-options';
import * as citiesApi from '@/features/cities/cities.api';
import { formatDMY } from '@/lib/date-format';

const NONE = '__none__';

interface FlightDraft {
  fromCityId: string | null;
  toCityId: string | null;
  /** Airport picked when the city lists neighbouring airports; null = the city's own code. */
  fromAirportCode: string | null;
  toAirportCode: string | null;
  airlineId: string;
  flightNumber: string;
  departureDate: string;
  departureTime: string;
  arrivalDate: string;
  arrivalTime: string;
  /** Per-pax block fares in RUPEES (converted to paise on save). */
  adultPrice: string;
  childPrice: string;
  infantPrice: string;
}

/** Rupees string -> paise string; blank/NaN -> "0". */
const rupeesToPaise = (r: string): string => String(Math.round((Number(r) || 0) * 100));
/** Paise string -> rupees string for editing; "0" shows blank. */
const paiseToRupees = (p: string): string => {
  const n = Number(p) / 100;
  return n === 0 ? '' : String(n);
};

const emptyDraft = (): FlightDraft => ({
  fromCityId: null,
  toCityId: null,
  fromAirportCode: null,
  toAirportCode: null,
  airlineId: NONE,
  flightNumber: '',
  departureDate: '',
  departureTime: '',
  arrivalDate: '',
  arrivalTime: '',
  adultPrice: '',
  childPrice: '',
  infantPrice: '',
});

const toDraft = (f: FixedDepartureFlightOut): FlightDraft => ({
  fromCityId: f.fromCityId,
  toCityId: f.toCityId,
  fromAirportCode: f.fromAirportCode ?? null,
  toAirportCode: f.toAirportCode ?? null,
  airlineId: f.airlineId ?? NONE,
  flightNumber: f.flightNumber ?? '',
  departureDate: f.departureDate ?? '',
  departureTime: f.departureTime ?? '',
  arrivalDate: f.arrivalDate ?? '',
  arrivalTime: f.arrivalTime ?? '',
  adultPrice: paiseToRupees(f.adultPricePaise),
  childPrice: paiseToRupees(f.childPricePaise),
  infantPrice: paiseToRupees(f.infantPricePaise),
});

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** Zero-pad a typed time to HH:mm on blur so "9:05" is accepted as "09:05". */
const normalizeTime = (v: string): string => {
  const t = v.trim();
  if (!t) return '';
  const [h, m, ...rest] = t.split(/[:.]/);
  if (
    rest.length === 0 &&
    h !== undefined &&
    m !== undefined &&
    /^\d{1,2}$/.test(h) &&
    /^\d{1,2}$/.test(m)
  ) {
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  }
  return t;
};

const toPayload = (d: FlightDraft) => ({
  fromCityId: d.fromCityId as string,
  toCityId: d.toCityId as string,
  fromAirportCode: d.fromAirportCode,
  toAirportCode: d.toAirportCode,
  airlineId: d.airlineId === NONE ? null : d.airlineId,
  flightNumber: orNull(d.flightNumber),
  departureDate: orNull(d.departureDate),
  departureTime: orNull(d.departureTime),
  arrivalDate: orNull(d.arrivalDate),
  arrivalTime: orNull(d.arrivalTime),
  adultPricePaise: rupeesToPaise(d.adultPrice),
  childPricePaise: rupeesToPaise(d.childPrice),
  infantPricePaise: rupeesToPaise(d.infantPrice),
});

const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const ymdToDate = (v: string): Date | undefined => (v ? new Date(`${v}T00:00:00.000Z`) : undefined);
const dateToYmd = (d: Date | null | undefined): string =>
  d ? new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10) : '';

const fmtDate = (v: string | null): string => formatDMY(v) || 'No date';

/**
 * "Airport" select under a city box: shown only when the city master lists
 * neighbouring airports for the city. Empty = the city's own code.
 */
const AirportSelect = ({
  cityId,
  value,
  onChange,
}: {
  cityId: string | null;
  value: string | null;
  onChange: (next: string | null) => void;
}): React.JSX.Element | null => {
  const city = useQuery({
    queryKey: ['fd-flight-city', cityId],
    queryFn: () => citiesApi.getCity(cityId as string),
    enabled: Boolean(cityId),
    staleTime: 60_000,
  });
  const airports = airportOptionsOf(city.data);
  if (airports.length < 2) return null;
  const own = city.data?.iataCode ?? null;
  return (
    <div className="mt-2">
      <UzSelect
        label="Airport"
        value={value ?? own ?? ''}
        onValueChange={(v) => onChange(v === own ? null : v)}
        options={airports}
      />
    </div>
  );
};

/**
 * Block flight OPTION POOL. Unlike hotels these DO carry real dates: block
 * inventory only exists on specific departures, so at itinerary time the
 * operator either picks one of these blocks or searches live TBO instead.
 */
export const FdFlightsStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const createFlight = useCreateFixedDepartureFlight(fd.id);
  const updateFlight = useUpdateFixedDepartureFlight(fd.id);
  const deleteFlight = useDeleteFixedDepartureFlight(fd.id);
  const airlines = useAirlinesList({ page: 1, pageSize: 200, sortDir: 'asc', isActive: true });

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<FlightDraft>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<FlightDraft>(emptyDraft);

  const airlineOptions = [
    { value: NONE, label: 'No airline' },
    ...(airlines.data?.data ?? []).map((a) => ({ value: a.id, label: a.name })),
  ];

  const valid = (d: FlightDraft): boolean => Boolean(d.fromCityId && d.toCityId);

  const submitNew = async (): Promise<void> => {
    if (!valid(draft)) return;
    await createFlight.mutateAsync(toPayload(draft));
    setDraft(emptyDraft());
    setAdding(false);
  };

  const submitEdit = async (): Promise<void> => {
    if (!editingId || !valid(editDraft)) return;
    await updateFlight.mutateAsync({ flightId: editingId, payload: toPayload(editDraft) });
    setEditingId(null);
  };

  const form = (d: FlightDraft, set: (patch: Partial<FlightDraft>) => void): React.JSX.Element => (
    <div className="space-y-3">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[10px] text-zinc-400 uppercase">From</p>
          <CityPicker
            label={undefined}
            value={d.fromCityId}
            onValueChange={(v) => set({ fromCityId: v, fromAirportCode: null })}
          />
          <AirportSelect
            cityId={d.fromCityId}
            value={d.fromAirportCode}
            onChange={(v) => set({ fromAirportCode: v })}
          />
        </div>
        <ArrowRight className="mb-2.5 h-4 w-4 shrink-0 text-zinc-300" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[10px] text-zinc-400 uppercase">To</p>
          <CityPicker
            label={undefined}
            value={d.toCityId}
            onValueChange={(v) => set({ toCityId: v, toAirportCode: null })}
          />
          <AirportSelect
            cityId={d.toCityId}
            value={d.toAirportCode}
            onChange={(v) => set({ toAirportCode: v })}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
        <div className="lg:col-span-2">
          <UzSelect
            label="Airline"
            value={d.airlineId}
            onValueChange={(v) => set({ airlineId: v })}
            options={airlineOptions}
          />
        </div>
        <UzInput
          label="Flight no."
          placeholder="6E-231"
          value={d.flightNumber}
          onChange={(e) => set({ flightNumber: e.target.value })}
        />
        <div>
          <p className="mb-1 text-sm font-medium">Departure date</p>
          <UzDatePicker
            displayFormat="dd-MM-yyyy"
            placeholder="Departure"
            minDate={startOfToday()}
            value={ymdToDate(d.departureDate)}
            onValueChange={(date) => set({ departureDate: dateToYmd(date) })}
          />
        </div>
        <UzInput
          label="Dep. time"
          placeholder="17:00"
          value={d.departureTime}
          onChange={(e) => set({ departureTime: e.target.value })}
          onBlur={(e) => set({ departureTime: normalizeTime(e.target.value) })}
        />
        <div>
          <p className="mb-1 text-sm font-medium">Arrival date</p>
          <UzDatePicker
            displayFormat="dd-MM-yyyy"
            placeholder="Arrival"
            minDate={ymdToDate(d.departureDate) ?? startOfToday()}
            value={ymdToDate(d.arrivalDate)}
            onValueChange={(date) => set({ arrivalDate: dateToYmd(date) })}
          />
        </div>
        <UzInput
          label="Arr. time"
          placeholder="19:35"
          value={d.arrivalTime}
          onChange={(e) => set({ arrivalTime: e.target.value })}
          onBlur={(e) => set({ arrivalTime: normalizeTime(e.target.value) })}
        />
      </div>
      <div>
        <p className="mb-1 text-[10px] tracking-wide text-zinc-400 uppercase">
          Block fares (per pax, INR) - optional add-on
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <UzInput
            label="Adult (12+)"
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={d.adultPrice}
            onChange={(e) => set({ adultPrice: e.target.value })}
          />
          <UzInput
            label="Child (2-12)"
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={d.childPrice}
            onChange={(e) => set({ childPrice: e.target.value })}
          />
          <UzInput
            label="Infant (0-2)"
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={d.infantPrice}
            onChange={(e) => set({ infantPrice: e.target.value })}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">Block flight options</h2>
          <p className="mt-0.5 text-xs text-zinc-500">
            Block inventory held for specific dates. When building an itinerary the operator picks
            one of these or searches live flights instead.
          </p>
        </div>
        {!adding && (
          <UzButton size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Add flight
          </UzButton>
        )}
      </div>

      {adding && (
        <section className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4">
          {form(draft, (patch) => setDraft((p) => ({ ...p, ...patch })))}
          <div className="mt-3 flex items-center gap-2">
            <UzButton
              size="sm"
              onClick={submitNew}
              disabled={!valid(draft)}
              loading={createFlight.isPending}
            >
              Add flight
            </UzButton>
            <UzButton
              size="sm"
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setDraft(emptyDraft());
              }}
            >
              Cancel
            </UzButton>
          </div>
        </section>
      )}

      {fd.flights.length === 0 && !adding ? (
        <UzEmptyState
          icon={<Plane className="h-6 w-6" aria-hidden />}
          title="No block flights yet"
          description="Add the held inventory for this fixed departure, with the dates it is available on."
        />
      ) : (
        <div className="space-y-2">
          {fd.flights.map((flight) =>
            editingId === flight.id ? (
              <section key={flight.id} className="rounded-lg border border-zinc-300 bg-white p-4">
                {form(editDraft, (patch) => setEditDraft((p) => ({ ...p, ...patch })))}
                <div className="mt-3 flex items-center gap-2">
                  <UzButton
                    size="sm"
                    onClick={submitEdit}
                    disabled={!valid(editDraft)}
                    loading={updateFlight.isPending}
                  >
                    Save
                  </UzButton>
                  <UzButton size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </UzButton>
                </div>
              </section>
            ) : (
              <div
                key={flight.id}
                className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white px-4 py-3"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => {
                    setEditingId(flight.id);
                    setEditDraft(toDraft(flight));
                  }}
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                    <span className="truncate">
                      {flight.fromCityName ?? 'Unknown'}
                      {flight.fromAirportCode ? ` (${flight.fromAirportCode})` : ''}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-zinc-300" aria-hidden />
                    <span className="truncate">
                      {flight.toCityName ?? 'Unknown'}
                      {flight.toAirportCode ? ` (${flight.toAirportCode})` : ''}
                    </span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-zinc-500">
                    <span>{flight.airlineName ?? 'No airline'}</span>
                    {flight.flightNumber && (
                      <>
                        <span className="text-zinc-300">|</span>
                        <span className="font-mono">{flight.flightNumber}</span>
                      </>
                    )}
                    <span className="text-zinc-300">|</span>
                    <span>
                      {fmtDate(flight.departureDate)}
                      {flight.departureTime ? ` ${flight.departureTime}` : ''}
                      {' - '}
                      {fmtDate(flight.arrivalDate)}
                      {flight.arrivalTime ? ` ${flight.arrivalTime}` : ''}
                    </span>
                  </div>
                </button>
                <UzButton
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-zinc-400 hover:text-red-600"
                  onClick={() => deleteFlight.mutate(flight.id)}
                  aria-label="Remove flight"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </UzButton>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};
